import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import logo from "../../Images/logo.png";
import { useFormik } from "formik";
import { forgotPasswordSchema } from "../../Schemas/forgotPassword";
import { useTanstackApiCall } from "../../Api/ReactQueryApiCall";
import CooldownTimer from "../Helper/CooldownTimer";
import {
  getLocalStorage,
  saveLocalStorage,
} from "../../Utility/saveLocalStorage";
const localStorageKeyForgotPassword = "FORGOT_PASSWORD_COOLDOWN";
const forgotPasswordCooldownTime = 1; // Cooldown time in minutes

const ForgotPassword = () => {
  // Retrieve cooldown time from local storage
  const forgotPasswordCooldownGet = getLocalStorage(
    localStorageKeyForgotPassword
  );
  const [forgotCoolDownTime, setForgotCoolDownTime] = useState(
    forgotPasswordCooldownGet
  );
  const [isCooldownActive, setIsCooldownActive] = useState(false);

  const bgCardDynamic = useSelector((state) => state.setMode).css
    ?.cardBackground;
  const textFormAlertLightMode = useSelector((state) => state.setMode).css
    ?.textFormAlertColor;

  const initialValues = {
    email: forgotCoolDownTime?.email ? forgotCoolDownTime?.email : "",
  };

  // Call the custom hook for forgotPasswordApiStatus
  const forgotPasswordApiStatus = useTanstackApiCall(
    "/public/user/forgot-password",
    null,
    "POST",
    null
  );

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues,
      validationSchema: forgotPasswordSchema,
      onSubmit: async (values) => {
        const payload = { email_id: values.email };

        try {
          // Check if cooldown is still active
          if (forgotPasswordCooldownGet) {
            const cooldownComplete =
              forgotCoolDownTime?.time &&
              new Date(forgotCoolDownTime.time) < new Date();

            if (!cooldownComplete) {
              alert("You are on cooldown!");
              return;
            }
          }

          // Call the forgot password API
          await forgotPasswordApiStatus.mutateAsync(payload);

          // If the request is successful, set a new cooldown
          const now = new Date();
          now.setMinutes(now.getMinutes() + forgotPasswordCooldownTime);
          const cooldownEndTime = now.toISOString();

          // Save the new cooldown time to local storage and update state
          saveLocalStorage(localStorageKeyForgotPassword, {
            time: cooldownEndTime,
            email: values.email,
          });
          setForgotCoolDownTime({ time: cooldownEndTime });
          setIsCooldownActive(true); // Activate the cooldown state
        } catch (error) {
          console.error("Error occurred:", error);
        }
      },
    });

  // Effect to check cooldown status
  useEffect(() => {
    if (forgotCoolDownTime?.time) {
      const cooldownEndTime = new Date(forgotCoolDownTime.time);
      const currentTime = new Date();
      setIsCooldownActive(cooldownEndTime > currentTime);
    }
  }, [forgotCoolDownTime]);

  const apiCallMsg =
    forgotPasswordApiStatus.data?.msg ||
    forgotPasswordApiStatus.error?.response?.data?.msg ||
    "An unexpected error occurred!";

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8 mt-16">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            className="mx-auto h-16 w-auto rounded-lg shadow-lg"
            loading="lazy"
            src={logo}
            alt="Company logo"
          />
          <h2 className="mt-3 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Retrieve Your Account Access
          </h2>
        </div>

        <div className="mt-4">
          <h5 className="text-Brand-Color font-extrabold text-center block underline">
            Welcome to jsonOnClouds
          </h5>
        </div>

        <div
          className={`${bgCardDynamic} mt-2 sm:mx-auto sm:w-full sm:max-w-sm pb-12 pt-6 px-6 rounded-xl shadow-2xl`}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm text-Brand-Color leading-6 text-white/90 font-extrabold"
              >
                Email address
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="block px-2 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#ffa200d7] outline-none sm:text-sm sm:leading-6"
                />
                {errors.email && touched.email && (
                  <small
                    className={`${textFormAlertLightMode} text-xs font-semibold`}
                  >
                    {errors.email}
                  </small>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="flex w-full justify-center rounded-md bg-Brand-Color hover:bg-Brand-Color-Light px-3 py-1.5 text-sm leading-6 text-white font-bold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffa200e8]"
              //   disabled={isCooldownActive} // Disable button during cooldown
            >
              {forgotPasswordApiStatus.isPending ? "Loading..." : "Send Link"}
            </button>
            {forgotPasswordApiStatus.isError && (
              <small className={`text-red-500`}>{apiCallMsg}</small>
            )}
            {forgotPasswordApiStatus.isSuccess && (
              <small className={`text-green-500`}>{apiCallMsg}</small>
            )}

            {isCooldownActive && (
              <CooldownTimer
                endTimeUtc={forgotCoolDownTime.time}
                message={`Password reset requested. Try again in ${forgotPasswordCooldownTime} minutes if you didn’t get the email.`}
              />
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
